import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Truck } from 'lucide-react';
import { restaurantInfo, openingHours, deliveryZones } from '../data/mock';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <h3 className="text-2xl font-black mb-4">
              <span className="text-[#00BCD4]">Boldog</span>
              <span className="text-[#FFA726]"> Büfé</span>
            </h3>
            <p className="text-gray-400 mb-4 italic">
              "{restaurantInfo.tagline}"
            </p>
            <div className="flex items-center space-x-1 text-[#FFA726]">
              <span className="text-2xl">⭐</span>
              <span className="font-bold text-xl">{restaurantInfo.rating}</span>
              <span className="text-gray-400 text-sm">({restaurantInfo.reviewCount} vélemény)</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4">Gyors linkek</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Főoldal
                </Link>
              </li>
              <li>
                <Link to="/menu" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Étlap
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Galéria
                </Link>
              </li>
              <li>
                <Link to="/reviews" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Vélemények
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Rólunk
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-[#00BCD4] transition-colors">
                  Kapcsolat
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4">Elérhetőség</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#00BCD4] flex-shrink-0 mt-1" />
                <div className="text-gray-400 text-sm">
                  <p>{restaurantInfo.address.zip} {restaurantInfo.address.city}</p>
                  <p>{restaurantInfo.address.street}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-[#00BCD4] flex-shrink-0" />
                <a
                  href={`tel:${restaurantInfo.phone}`}
                  className="text-gray-400 hover:text-[#00BCD4] transition-colors text-sm"
                >
                  {restaurantInfo.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="text-lg font-bold mb-4">
              <Clock className="w-5 h-5 inline-block mr-2" />
              Nyitvatartás
            </h4>
            <ul className="space-y-1 text-sm">
              {openingHours.map((day) => (
                <li key={day.day} className="flex justify-between">
                  <span className="text-gray-400">{day.day}:</span>
                  <span className={day.hours === 'Zárva' ? 'text-red-400' : 'text-gray-300'}>
                    {day.hours}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Delivery Zones Section */}
        <div className="mt-10 pt-10 border-t border-gray-800">
          <div className="flex items-center justify-center mb-4">
            <Truck className="w-6 h-6 text-[#FFA726] mr-3" />
            <h4 className="text-xl font-bold text-center">Kiszállítási területek</h4>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {deliveryZones.map((zone) => (
              <div
                key={zone.id}
                className="px-4 py-2 bg-gray-800 rounded-full text-gray-300 text-sm font-medium hover:bg-gray-700 transition-colors"
              >
                {zone.name}
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-xs mt-4">
            Házhozszállítás a fenti településekre. Részletek: <a href="tel:+36309517159" className="text-[#00BCD4] hover:underline">+36 30 951 7159</a>
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-gray-800">
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <Link 
              to="/aszf" 
              className="text-gray-400 hover:text-[#00BCD4] transition-colors text-sm"
            >
              ÁSZF
            </Link>
            <span className="text-gray-600">|</span>
            <Link
              to="/adatvedelmi-tajekoztato"
              className="text-gray-400 hover:text-[#00BCD4] transition-colors text-sm"
            >
              Adatvédelem
            </Link>
            <span className="text-gray-600">|</span>
            <Link 
              to="/impresszum" 
              className="text-gray-400 hover:text-[#00BCD4] transition-colors text-sm"
            >
              Impresszum
            </Link>
          </div>
          {/* Copyright */}
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-center md:text-left text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} Boldog Büfé. Minden jog fenntartva.
            </p>
            <div className="flex items-center space-x-2 bg-gradient-to-r from-blue-900 to-blue-800 px-4 py-2 rounded-lg">
              <span className="text-2xl">💳</span>
              <p className="text-white font-bold text-sm">
                OTP SZÉP-kártya elfogadóhely
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
